import { Dimensions, NativeModules, PixelRatio, Platform } from 'react-native';
import { useEffect, useRef, useState } from 'react';

import Auth from '../../../src/auth/Auth';
import AuthService from '../services/AuthService';
// @ts-ignore
import { Base64 } from 'js-base64';
import Environment from '../Environment';
import { isUrlAuthorizedForSubsplashDomains } from '../services/AuthService/utilities';
import moment from 'moment-timezone';
import { version } from '@omni/version';

const debug = require('debug')('omni:kit:utilities');

const { width, height } = Dimensions.get('window');

export const SCREEN_WIDTH = width;
export const SCREEN_HEIGHT = height;

export const bytesPerMb = 1000000;
export const maxFileSizeInMb = 5;
export const fileTooLargeMessage =
  'This file exceeds the maximum file size ' +
  `(${maxFileSizeInMb}MB) and could not be sent`;

export enum ImageServiceType {
  ImageJpeg,
  ImagePng,
  BlurJpeg,
  BlurPng,
  Metadata,
  WhitePng,
}

export const logButtonPressEvent = (buttonAction: string): void => {
  logEvent('button_press', { button_action: buttonAction });
};

export const logItemPressEvent = (itemAction: string): void => {
  logEvent('item_press', { item_action: itemAction });
};

export const logEvent = (
  event: string,
  params?: Record<string, unknown>
): void => {
  try {
    NativeModules.ReactPlatformBridge.logEvent(event, params);
  } catch {
    console.warn(`Could not log event: ${event}`);
  }
};

export const transparentHexColor = (color: string, alpha: string) => {
  return `${color}${alpha}`;
};

export const parseImageUrl = (
  url: string,
  width: number,
  height: number,
  type: ImageServiceType,
  name: string | null = null
) => {
  let imageUrl = url;

  if (!url || url.length === 0) return '';

  const pixelRatio = PixelRatio.get();
  const scaledWidth = Math.round(Math.round(width * pixelRatio)).toString();
  const scaledHeight = Math.round(Math.round(height * pixelRatio)).toString();

  imageUrl = imageUrl.replace('{width}', scaledWidth);
  imageUrl = imageUrl.replace('{height}', scaledHeight);

  name = name || 'image';
  let ext = 'jpg';
  switch (type) {
    case ImageServiceType.ImageJpeg:
      name = 'image';
      ext = 'jpg';
      break;
    case ImageServiceType.ImagePng:
      name = 'image';
      ext = 'png';
      break;
    case ImageServiceType.BlurJpeg:
      name = 'blur';
      ext = 'jpg';
      break;
    case ImageServiceType.BlurPng:
      name = 'blur';
      ext = 'png';
      break;
    case ImageServiceType.Metadata:
      name = 'meta';
      ext = 'jpg';
      break;
    case ImageServiceType.WhitePng:
      name = 'fit-white';
      ext = 'png';
    default:
      break;
  }
  imageUrl = imageUrl.replace('{name}', name);
  imageUrl = imageUrl.replace('{ext}', ext);

  return imageUrl;
};

export const createImageUrl = (id?: string): string | undefined => {
  if (!id) {
    return undefined;
  }

  return `${Environment.imagesHost}/{name}.{ext}?id=${id}&w={width}&h={height}`;
};

export const getImageId = (url: string): string | undefined => {
  if (url && url.includes('?')) {
    const params = getQueryParams({ search: '?' + url.split('?')[1] });

    return params.id;
  }
};

export const imageTypeFromContentType = (contentType: string) => {
  switch (contentType) {
    case 'image/png':
      return ImageServiceType.ImagePng;
    case 'image/jpg':
    case 'image/jpeg':
      return ImageServiceType.ImageJpeg;
    default:
      return ImageServiceType.ImageJpeg;
  }
};

export const b64encode = (toEncode: string) => {
  let encoded = Base64.encode(toEncode);

  // RFC 4648 Spec where '+' is encoded as '-' and '/' is encoded as '_'.
  encoded = encoded.replace(/\+/g, '-');
  encoded = encoded.replace(/\//g, '_');

  return encoded;
};

export const dateFormatter = (
  timestamp: string,
  format: string,
  IANATimeZone?: string
) => {
  let date = moment(timestamp);

  if (IANATimeZone) {
    date = date.tz(IANATimeZone);
  }

  const dateComponent = date.format(format);

  return dateComponent;
};

// @ts-ignore
export function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

// usage example: optimize rendering performance during window resize on web or iPad split view by
// only applying new size if the image width has changed by a factor of 1.5x or if the aspect ratio has changed
// this also prevents the app from re-downloading the same image in rapid succession as the size of the viewport changes
export const useCoalescedSize = (
  width: number,
  height: number
): { coalescedWidth: number; coalescedHeight: number } => {
  const [coalescedWidth, setCoalescedWidth] = useState(width > 0 ? width : 1); // default to 1 to prevent divide by zero error
  const [coalescedHeight, setCoalescedHeight] = useState(
    height > 0 ? height : 1
  );

  useEffect(() => {
    if (width > 0 && height > 0) {
      const aspectRatio = height > 0 ? width / height : 0;
      const coalescedAspectRatio =
        coalescedHeight > 0 ? coalescedWidth / coalescedHeight : 0;

      const scaleFactorDelta =
        Math.max(width, coalescedWidth) / Math.min(width, coalescedWidth);
      const aspectRatioDelta =
        Math.max(aspectRatio, coalescedAspectRatio) /
        Math.min(aspectRatio, coalescedAspectRatio);

      if (scaleFactorDelta >= 1.5 || aspectRatioDelta >= 1.01) {
        setCoalescedWidth(width);
        setCoalescedHeight(height);
      }
    }
  }, [width, height, coalescedWidth, coalescedHeight]);

  return { coalescedWidth, coalescedHeight };
};

/**
 * A utility for subsplash/internal requests that require end user authorization
 * with the subsplash auth provider.
 *
 * Alternatively, the kit Downloader can be used, but caller is responsible
 * for validating/authorizing content domain to avoid including Authorization
 * to potentially malicious third pary backends.
 *
 * TODO: refactor
 * - Rewrite utilities.ts and AuthService.ts into custom hooks and a functional component
 * - Unify and simplify all business logic in auth flow.
 *
 * This refactor will result in significant regression risk, so wait until there is enough time
 * to add unit test coverage and manual QA to validate all the EUA flows.
 *
 * @param url
 * @param config Misc props such as 'headers' and 'body', if needed for the request
 * @param isRetry An internal flag that tracks whether token refresh has occureed. External callers should not provide this value.
 * @returns
 */
export const fetchWithAuth = async (
  url: string,
  config?: RequestInit,
  isRetry = false
): Promise<Response> => {
  /**
   * Note that this will prevent clients from letting users change
   * their profile information using third party updateUrls
   * which we should probably not allow anyways.
   */
  const authorized = isUrlAuthorizedForSubsplashDomains(url);

  debug(`Domain authorized: ${authorized} for url: ${url}`);

  if (!authorized) {
    return Promise.reject();
  }

  const appHeaders = {
    'sap-app-version': version, // required to support confirmation code field request on 5.13+
    'sap-omni-version': version, // required to support confirmation code field request on 5.13+
    'sap-device-os': Platform.OS, // required to support confirmation code field request on 5.13+
  };

  if (Platform.OS === 'web') {
    // add web headers
    // @ts-ignore
    appHeaders['sap-platform'] = 'web';
  }

  const headers = {
    ...appHeaders,
    ...config?.headers,
    // usage: access token must be prefixed with "Bearer "
    Authorization: AuthService.accessToken || '',
  };

  const res = await fetch(url, {
    ...config,
    headers: headers,
  });

  if (!res.ok && (res.status === 401 || res.status === 403) && !isRetry) {
    await AuthService.refreshAccessToken();

    return fetchWithAuth(url, config, true /*retry*/);
  }

  return res;
};

export function handleErrorResponse(response: Response) {
  return response.text().then((text) => {
    try {
      const json = JSON.parse(text);

      return Promise.resolve({
        status: response.status,
        message: Array.isArray(json.errors)
          ? json.errors[0].detail
          : json.error_description,
        data: json,
      });
    } catch (e) {
      return Promise.resolve({
        status: response.status,
        message: text,
        data: null,
      });
    }
  });
}

export const getQueryParams = (location?: {
  hash?: string;
  key?: string;
  pathname?: string;
  search?: string;
}): Record<string, string> => {
  const paramObj: Record<string, string> = {};

  if (location?.search) {
    const noQuestion = location.search.substring(1);
    const queries = noQuestion.split('&');

    queries.forEach((query) => {
      const parts = query.split('=');

      if (parts.length === 2) {
        const [key, value] = parts;
        paramObj[key] = value;
      }
    });
  }

  return paramObj;
};

export function capitalizeFirstLetter(message: string) {
  return message.length > 0
    ? message.charAt(0).toUpperCase() + message.slice(1)
    : '';
}

export const isAppKeyTCA = (appKey?: string): boolean => {
  return appKey?.toLowerCase() === 'gh936h';
};

export const isWithinIframe = (): boolean => {
  return (
    Platform.OS === 'web' &&
    (window.frameElement !== null || window.top !== window)
  );
};

/*
 * If within an iframe, sends a postMessage with the content height so the parent
 * window can resize the iframe accordingly.
 * (Min height: 600px)
 *
 * The post message is handled by the Subsplash embed script:
 * https://subsplash.io/ember/web-client/-/blob/main/public/external/embed-1.1.0.js#L93-95
 * */
export const postContentHeight = (height?: number): void => {
  if (!isWithinIframe() || !height) {
    return;
  }

  const MIN_CONTENT_HEIGHT = 600;
  window.parent.postMessage(
    {
      pageHeight: Math.max(MIN_CONTENT_HEIGHT, height),
    },
    '*'
  );
};
